import { useEffect, useState } from "react";
import {
  getCoachDashboardStats,
  getCoachTeamsList,
  getTeamSurveyData,
  CoachDashboardStats,
  CoachTeamFromApi,
  TeamSurveyMember,
} from "../../coach/api/coach.api";
import CoachStatsBar from "./CoachStatsBar";
import CoachWelcomeBanner from "./CoachWelcomeBanner";
import MyTeamsGrid from "./MyTeamsGrid";
import { CoachTeam } from "./TeamCard";
import { useCoach } from "../../coach/contexts/CoachProvider";

const CoachDashboard: React.FC = () => {
  const { organizationId, defaultSurveyId: questionnaireId } = useCoach();

  const [stats, setStats] = useState<CoachDashboardStats>({
    totalActiveUsers: 0,
    totalDepartments: 0,
    totalTeams: 0,
  });

  const [teams, setTeams] = useState<CoachTeam[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [teamsLoading, setTeamsLoading] = useState(true);

  useEffect(() => {
    if (!organizationId || !questionnaireId) return;

    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const res = await getCoachDashboardStats(organizationId);
        if (res?.success && res.data) setStats(res.data);
      } catch (err) {
        console.warn("Failed to fetch stats", err);
      } finally {
        setStatsLoading(false);
      }
    };

    const fetchTeams = async () => {
      setTeamsLoading(true);
      try {
        const listRes = await getCoachTeamsList(organizationId);
        if (!listRes?.success || !listRes.data?.rows) return;

        const surveyResults = await Promise.allSettled(
          listRes.data.rows.map((team: CoachTeamFromApi) =>
            getTeamSurveyData(team.id, questionnaireId),
          ),
        );

        const enrichedTeams: CoachTeam[] = listRes.data.rows.map(
          (team: CoachTeamFromApi, index: number) => {
            const surveyResult = surveyResults[index];
            const surveyData =
              surveyResult.status === "fulfilled" && surveyResult.value?.success
                ? surveyResult.value.data
                : null;

            return {
              id: team.id,
              name: team.name,
              teamLead: team.team_lead
                ? { name: team.team_lead.name }
                : undefined,
              //  members from survey response (includes images + completion)
              members:
                surveyData?.members.map((m: TeamSurveyMember) => ({
                  id: m.id,
                  first_name: m.first_name,
                  last_name: m.last_name,
                  image: m.image,
                  completed_per: m.completed_per,
                })) ?? [],
              //  real completion % from survey
              completionPercentage: surveyData?.team_compl_percentage ?? 0,
            };
          },
        );

        setTeams(enrichedTeams);
      } catch (err) {
        console.warn("Failed to fetch teams", err);
      } finally {
        setTeamsLoading(false);
      }
    };

    Promise.all([fetchStats(), fetchTeams()]);
  }, [organizationId, questionnaireId]);

  return (
    <div className="flex flex-col gap-6">
      <CoachWelcomeBanner />
      <CoachStatsBar stats={stats} loading={statsLoading} />
      <MyTeamsGrid teams={teams} loading={teamsLoading} />
    </div>
  );
};

export default CoachDashboard;
